import { FormlyFieldConfig } from '@ngx-formly/core';
import {
  IDatasetObject,
  IFormbuilderFieldTypeConfiguration,
  IIntTelInputConfigurationFormModel,
  IntTelInputConfiguration,
} from '../../../models/form-builder-types-configurations.model';
import {
  baseFieldParseIntoSettingsFormModel,
  baseFieldUpdateFromSettingsFormModel,
  getDefaultBaseSettingsConfigFormly,
} from '../form-fields-formly-settings/form-builder-default-settings-configuration-formly-fields.utils';
import {
  getRequiredValidatorFormConfig,
  getUniqueFieldValidatorFormConfig,
  getValidationFieldsFormConfig,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-generator.util';
import { IFormFieldsValidationModel } from '../../../models/form-fields-formly-validation.model';
import {
  parseInvalidIntlPhoneNumberFormatValidatorParseFieldIntoModel,
  parseRequiredValidatorParseFieldIntoModel,
  parseUniqueFieldValidatorFormDataIntoModel,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-parse-to-model.util';
import {
  setInvalidIntlPhoneNumberFormatUpdateFieldFromModel,
  setRequiredValidatorUpdateFieldFromModel,
  setUniqueFieldValidatorFormDataIntoField,
} from '../form-fields-formly-validations/form-fields-formly-validation-field-set-from-model.util';
import { FormBuilderService } from '../../../services/form-builder.service';
import { getFieldDisplayFormConfig } from '../form-fields-formly-display/form-fields-formly-display-field-generator.util';
import { IFormFieldsDisplayModel } from '../../../models/form-fields-formly-display.model';
import { parseFieldIntoDisplayFormModel } from '../form-fields-formly-display/form-fields-formly-display-field-parse-to-model.util';
import { setDisplayHideExpressionFromModel } from '../form-fields-formly-display/form-fields-formly-display-field-set-from-model.util';
import { fieldsBaseConfigurationFormModel } from './shared.configuration';
import { getConditionalValidationsFormConfig } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-generator.util';
import { EAvailableConditionalValidations } from '../../../models/form-fields-formly-conditional-validation.model';
import { parseConditionalValidatorsModelFromFieldIntoModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-parse-to-model.util';
import { setConditionalExpressionFromModel } from '../form-fields-formly-validations/conditional-validations/fields-formly-conditional-validation-field-set-from-model.util';
import { EIremboFormlyFieldTypes } from '@irembo-andela/irembogov3-common';

export const defaultnternationalPhoneNumberConfigFormModel: IIntTelInputConfigurationFormModel =
  {
    ...fieldsBaseConfigurationFormModel(),
  };

export const internationalPhoneNumberFieldGetConfigurationConfig = (
  formBuilderServiceInstance: FormBuilderService
): IFormbuilderFieldTypeConfiguration => {
  return {
    settings: {
      formModel: defaultnternationalPhoneNumberConfigFormModel,
      formlyForm: [
        ...getDefaultBaseSettingsConfigFormly(formBuilderServiceInstance),
        ...getIntlTelInputSettings(),
      ],
    },
    validations: {
      formModel: {},
      formlyForm: [
        getRequiredValidatorFormConfig(),
        getUniqueFieldValidatorFormConfig(formBuilderServiceInstance),
        getValidationFieldsFormConfig(formBuilderServiceInstance),
        getConditionalValidationsFormConfig(
          [EAvailableConditionalValidations.required],
          formBuilderServiceInstance
        ),
      ],
    },
    display: {
      formModel: {},
      formlyForm: [...getFieldDisplayFormConfig(formBuilderServiceInstance)],
    },
  };
};

export const internationalPhoneNumberFieldParseIntoSettingsFormModel = (
  field: FormlyFieldConfig
): IIntTelInputConfigurationFormModel => {
  let intTelconfigs: IntTelInputConfiguration = {};
  if (field?.props) {
    intTelconfigs = {
      allowDropdown: field?.props['allowDropdown'] ?? true,
    };

    if (field?.props['configDetails']) {
      intTelconfigs = {
        ...intTelconfigs,
        initialCountry: field?.props['configDetails']['initialCountry'],
        excludeCountries: field?.props['configDetails']['excludeCountries'],
        onlyCountries: field?.props['configDetails']['onlyCountries'],
        preferredCountries: field?.props['configDetails']['preferredCountries'],
      };
    }
  }

  return {
    ...baseFieldParseIntoSettingsFormModel(field),
    ...intTelconfigs,
  };
};

export const internationalPhoneNumberFieldUpdateFromSettingsFormModel = (
  model: IIntTelInputConfigurationFormModel,
  internationalPhoneNumberField: FormlyFieldConfig
): FormlyFieldConfig => {
  const field = baseFieldUpdateFromSettingsFormModel(
    model,
    internationalPhoneNumberField
  );

  const formattedConfigs = transformPhoneNumberConfig(model);

  if (field.props) {
    field.props['initialCountry'] =
      formattedConfigs.simpliedConfigs.initialCountry;
    field.props['excludeCountries'] =
      formattedConfigs.simpliedConfigs.excludeCountries;
    field.props['preferredCountries'] =
      formattedConfigs.simpliedConfigs.preferredCountries;
    field.props['onlyCountries'] =
      formattedConfigs.simpliedConfigs.onlyCountries;
    field.props['allowDropdown'] = model.allowDropdown;
    field.props['configDetails'] = formattedConfigs.configDetails;
  }
  return field;
};

export const internationalPhoneNumberFieldParseIntoValidationFormModel = (
  field: FormlyFieldConfig
): IFormFieldsValidationModel => {
  let model: IFormFieldsValidationModel = {};

  model = parseRequiredValidatorParseFieldIntoModel(model, field);
  model = parseUniqueFieldValidatorFormDataIntoModel(model, field);
  model = parseInvalidIntlPhoneNumberFormatValidatorParseFieldIntoModel(
    model,
    field
  );
  model = parseConditionalValidatorsModelFromFieldIntoModel(model, field);

  return model;
};

export const internationalPhoneNumberFieldUpdateFromValidationFormModel = (
  model: IFormFieldsValidationModel,
  internationalPhoneNumberField: FormlyFieldConfig
): FormlyFieldConfig => {
  internationalPhoneNumberField = setRequiredValidatorUpdateFieldFromModel(
    model,
    internationalPhoneNumberField
  );

  internationalPhoneNumberField = setUniqueFieldValidatorFormDataIntoField(
    model,
    internationalPhoneNumberField
  );

  internationalPhoneNumberField =
    setInvalidIntlPhoneNumberFormatUpdateFieldFromModel(
      model,
      internationalPhoneNumberField
    );

  internationalPhoneNumberField = setConditionalExpressionFromModel(
    model,
    internationalPhoneNumberField
  );

  return internationalPhoneNumberField;
};

export const internationalPhoneNumberFieldParseFieldIntoDisplayFormModel = (
  field: FormlyFieldConfig
): IFormFieldsDisplayModel => {
  return parseFieldIntoDisplayFormModel(field);
};

export const internationalPhoneNumberFieldUpdateFromDisplayFormModel = (
  model: IFormFieldsDisplayModel,
  field: FormlyFieldConfig
): FormlyFieldConfig => {
  return setDisplayHideExpressionFromModel(model, field);
};

export const getIntlTelInputSettings = (): FormlyFieldConfig[] => {
  return [
    {
      key: 'initialCountry',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        label: 'Initial country',
        placeholder: 'Select initial country',
        options: [],
        multiple: false,
        useBaseUrl: true,
        dataset: {
          url: '/admin/v1/dataset-items/by-dataset-code/COUNTRY/paginated',
          dataField: 'data.content',
          pagination: 'true',
          searching: 'true',
          params: { size: 10, page: 0, sort: 'label', sortDirection: 'ASC' },
        },
        subType: 'CUSTOM_DROPDOWN_DATASET',
        bindLabel: 'label',
        bindValue: '',
        code: 'COUNTRY',
      },
    },
    {
      key: 'preferredCountries',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        label: 'Preffered countries',
        placeholder: 'Select preferred countries',
        options: [],
        multiple: true,
        useBaseUrl: true,
        dataset: {
          url: '/admin/v1/dataset-items/by-dataset-code/COUNTRY/paginated',
          dataField: 'data.content',
          pagination: 'true',
          searching: 'true',
          params: { size: 10, page: 0, sort: 'label', sortDirection: 'ASC' },
        },
        subType: 'CUSTOM_DROPDOWN_DATASET',
        bindLabel: 'label',
        bindValue: '',
        code: 'COUNTRY',
      },
    },
    {
      key: 'excludeCountries',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        label: 'Exclude countries',
        placeholder: 'Select countries to exclude',
        options: [],
        multiple: true,
        useBaseUrl: true,
        dataset: {
          url: '/admin/v1/dataset-items/by-dataset-code/COUNTRY/paginated',
          dataField: 'data.content',
          pagination: 'true',
          searching: 'true',
          params: { size: 10, page: 0, sort: 'label', sortDirection: 'ASC' },
        },
        subType: 'CUSTOM_DROPDOWN_DATASET',
        bindLabel: 'label',
        bindValue: '',
        code: 'COUNTRY',
      },
    },
    {
      key: 'onlyCountries',
      type: EIremboFormlyFieldTypes.customdropdownpaginated,
      props: {
        placeholder: 'Select specific countries to show in the list',
        label: 'Country whitelist',
        options: [],
        multiple: true,
        useBaseUrl: true,
        dataset: {
          url: '/admin/v1/dataset-items/by-dataset-code/COUNTRY/paginated',
          dataField: 'data.content',
          pagination: 'true',
          searching: 'true',
          params: { size: 10, page: 0, sort: 'label', sortDirection: 'ASC' },
        },
        subType: 'CUSTOM_DROPDOWN_DATASET',
        bindLabel: 'label',
        bindValue: '',
        code: 'COUNTRY',
      },
    },
    {
      key: 'allowDropdown',
      type: EIremboFormlyFieldTypes.checkbox,
      props: {
        label: 'Country dropdown list',
        placeholder: 'Read Only',
        formCheck: 'switch',
        description: 'Enables or disables country dropdown list',
      },
      id: 'allowDropdown',
    },
  ];
};

const transformPhoneNumberConfig = (
  model: IIntTelInputConfigurationFormModel
) => {
  const getSimplifiedCountries = (countries?: IDatasetObject[]) =>
    countries ? countries.map(country => country.value.toLowerCase()) : [];

  const getDetailedCountries = (countries?: IDatasetObject[]) =>
    countries ? countries.map(({ label, value }) => ({ label, value })) : [];

  const initialCountry = model.initialCountry;

  return {
    simpliedConfigs: {
      initialCountry: initialCountry?.value?.toLowerCase() || null,
      excludeCountries: getSimplifiedCountries(model.excludeCountries),
      onlyCountries: getSimplifiedCountries(model.onlyCountries),
      preferredCountries: getSimplifiedCountries(model.preferredCountries),
    },
    configDetails: {
      initialCountry: initialCountry
        ? { label: initialCountry?.label, value: initialCountry?.value }
        : null,
      excludeCountries: getDetailedCountries(model.excludeCountries),
      onlyCountries: getDetailedCountries(model.onlyCountries),
      preferredCountries: getDetailedCountries(model.preferredCountries),
    },
  };
};
